import type { FormikValues } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import FormDialog from '@components/Dialog/FormDialog';
import QuestionnaireForm from '@components/Questionnaire/Form';

import useQuestionnaire from '@hooks/useQuestionnaire';

import { getQuestionnaireResponseTemplate } from '@models/fhir-templates/questionnaire-response';

import { usePostQuestionnaireResponses } from 'generated/questionnaire.generated';

const messages = defineMessages({
  title: {
    id: 'questionnaire.title',
    defaultMessage: '{name} - Questionnaire',
  },
  submit: {
    id: 'GLOBAL.submit',
    defaultMessage: 'Submit',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
});

interface QuestionnaireDialogProps {
  /** Name of the patient */
  name?: string | null;
  /** ID of the patient to associate responses with */
  patient_id: string;
  /** Questionnaire identifier */
  identifier: string;
  /** Is the dialog open */
  isOpen: boolean;
  /** Callback for when the dialog closes */
  onClose: () => void;
}

/**
 * Component used to display a FHIR Questionnaire resource in a Dialog
 */
function QuestionnaireDialog({
  name,
  patient_id,
  identifier,
  isOpen,
  onClose,
}: QuestionnaireDialogProps) {
  const { formatMessage } = useIntl();
  const {
    data: questionnaireData,
    loading: questionnaireLoading,
    formHelpers,
  } = useQuestionnaire(
    {
      patient: patient_id,
      identifier,
    },
    !isOpen
  );
  const [createAnswer] = usePostQuestionnaireResponses();

  const questionnaire = questionnaireData?.getQuestionnaire.questionnaire;

  const loading = questionnaireLoading;

  const handleSubmit = async (values: FormikValues) => {
    const responses = formHelpers.mapValuesToItems(values);
    await createAnswer({
      variables: {
        input: {
          questionnaire_identifier: identifier,
          patient: patient_id,
          responses: getQuestionnaireResponseTemplate(patient_id, responses),
        },
      },
    });
  };

  return (
    <FormDialog
      open={isOpen}
      onClose={onClose}
      title={formatMessage(messages.title, { name: name ?? '' })}
      onSubmit={handleSubmit}
      loading={loading}
      validationSchema={formHelpers.validationSchema}
      initialValues={formHelpers.initialValues}
      content={<QuestionnaireForm data={questionnaire} />}
    />
  );
}

export default QuestionnaireDialog;
